"use client";

import Image from "next/image";
import Link from "next/link";
import {
  useEffect,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { useTheme } from "next-themes";

import { Footer, Nav } from "@/app/_components/sections";
import { IconCheck, IconCopy } from "@/app/_components/icons";
import { InstallSnippet } from "@/components/photon/install-snippet";
import { formatMatrixDate, slugify } from "./photon-data";
import {
  BENCHMARKS,
  latencySpread,
  throughputAdvantagePct,
  throughputMultiple,
} from "./performance-data";
import type {
  BaselineRuntime,
  BenchmarkDataset,
  BenchRuntime,
} from "./release-types";

const CONTACT_URL = "mailto:contact@moondream.ai";
const BASELINES: BaselineRuntime[] = ["vllm", "sglang"];
const RUNTIME_LOGOS: Record<BenchRuntime, string> = {
  photon: "/logos/moondream.png",
  vllm: "/logos/vllm.png",
  sglang: "/logos/sglang.png",
};

const runtimeName = (bench: BenchmarkDataset, runtime: BenchRuntime) =>
  bench.environment.runtimes[runtime].split(" ")[0];

const downloadCsv = (
  filename: string,
  header: string[],
  rows: (string | number)[][],
) => {
  const escape = (value: string | number) =>
    `"${String(value).replaceAll('"', '""')}"`;
  const csv = [header, ...rows]
    .map((cols) => cols.map(escape).join(","))
    .join("\n");
  const url = URL.createObjectURL(
    new Blob([csv], { type: "text/csv;charset=utf-8" }),
  );
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const ReproCommand = ({ command }: { command: string }) => {
  const onCopy = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (typeof navigator !== "undefined" && navigator.clipboard) {
      navigator.clipboard.writeText(command);
    }
    const el = e.currentTarget;
    el.dataset.copied = "1";
    setTimeout(() => {
      delete el.dataset.copied;
    }, 1200);
  };
  return (
    <div className="perf-repro">
      <span className="hero-install-prompt mono">$</span>
      <code className="perf-repro-cmd mono">{command}</code>
      <button
        type="button"
        className="hero-install-copy"
        aria-label="Copy reproduction command"
        onClick={onCopy}
      >
        <IconCopy size={13} />
      </button>
    </div>
  );
};

// Every chart carries its full provenance: model revisions, runtime
// versions, hardware and driver, workload, sampling, date, raw data, and
// the command that regenerates it.
const ChartPanel = ({
  eyebrow,
  title,
  sub,
  benchmark,
  runtimes,
  sampling,
  repro,
  onDownload,
  children,
}: {
  eyebrow: string;
  title: string;
  sub?: string;
  benchmark: BenchmarkDataset;
  runtimes?: BenchRuntime[];
  sampling: string;
  repro: string;
  onDownload: () => void;
  children: ReactNode;
}) => {
  const env = benchmark.environment;
  const runtimeIds =
    runtimes ?? (Object.keys(env.runtimes) as BenchRuntime[]);
  return (
    <div className="perf-chart">
      <div className="perf-chart-head">
        <div className="t-eyebrow" style={{ color: "var(--text-tertiary)" }}>
          {eyebrow}
        </div>
        <h3 className="t-heading-md" style={{ marginTop: 4 }}>
          {title}
        </h3>
        {sub ? (
          <p
            className="t-body-sm"
            style={{ color: "var(--text-secondary)", marginTop: 6, maxWidth: 680 }}
          >
            {sub}
          </p>
        ) : null}
      </div>
      {children}
      <dl className="perf-meta">
        <div>
          <dt>Models</dt>
          <dd>
            {benchmark.models.map((m) => `${m.model} (${m.revision})`).join(" · ")}
          </dd>
        </div>
        <div>
          <dt>Runtimes</dt>
          <dd>{runtimeIds.map((runtime) => env.runtimes[runtime]).join(" · ")}</dd>
        </div>
        <div>
          <dt>Hardware</dt>
          <dd>
            {env.gpu} · driver {env.driver} · CUDA {env.cuda}
          </dd>
        </div>
        <div>
          <dt>Workload</dt>
          <dd>{env.workload}</dd>
        </div>
        <div>
          <dt>Sampling</dt>
          <dd>{sampling}</dd>
        </div>
        <div>
          <dt>Date</dt>
          <dd>{formatMatrixDate(env.date)}</dd>
        </div>
      </dl>
      <div className="perf-actions">
        <button type="button" className="matrix-csv" onClick={onDownload}>
          Raw data (CSV)
        </button>
        <ReproCommand command={repro} />
      </div>
    </div>
  );
};

const BarLegend = ({
  benchmark,
  runtimes,
  note,
}: {
  benchmark: BenchmarkDataset;
  runtimes: BenchRuntime[];
  note?: string;
}) => (
  <div className="perf-legend">
    {runtimes.map((runtime) => (
      <span className="perf-key" key={runtime}>
        <span className={`perf-swatch is-${runtime}`} />{" "}
        {benchmark.environment.runtimes[runtime]}
      </span>
    ))}
    {note ? <span className="perf-key-note mono">{note}</span> : null}
  </div>
);

// ---- 01 · Request throughput: runtime × concurrency heatmaps per model ----
const ThroughputChart = ({ benchmark: bench }: { benchmark: BenchmarkDataset }) => {
  const maxMultiple = Math.max(
    ...bench.throughput.map((comparison) => comparison.multiple),
  );
  const heatStyle = (multiple: number) => {
    const normalized = Math.max(
      0,
      Math.min(1, (multiple - 1) / (maxMultiple - 1)),
    );
    return {
      "--perf-heat": `${24 + normalized * 24}%`,
    } as CSSProperties;
  };
  const formatMultiple = (multiple: number) =>
    `${multiple < 1.1 ? multiple.toFixed(2) : multiple.toFixed(1)}×`;

  return (
    <>
      <BarLegend
        benchmark={bench}
        runtimes={BASELINES.filter((runtime) =>
          bench.throughput.some((cell) => cell.runtime === runtime),
        )}
        note="Value = Photon throughput ÷ matched runtime"
      />
      <div className="tp-heatmap-list">
        {bench.models.map((model) => (
          <section className="tp-heatmap" key={model.id}>
            <div className="tp-heatmap-head">
              <span className="tp-heatmap-name">{model.model}</span>
              <span className="tp-heatmap-meta mono">
                {model.family} · rev {model.revision}
              </span>
            </div>
            <div className="tp-heatmap-grid">
              <span className="tp-heatmap-corner mono">Runtime</span>
              {bench.environment.concurrency.map((concurrency) => (
                <span className="tp-heatmap-column mono" key={concurrency}>
                  c{concurrency}
                </span>
              ))}
              {BASELINES.flatMap((runtime) => {
                const label = (
                  <span className="tp-heatmap-runtime mono" key={runtime}>
                    <i className={`perf-swatch is-${runtime}`} />
                    {runtimeName(bench, runtime)}
                  </span>
                );
                const values = bench.environment.concurrency.map(
                  (concurrency) => {
                    const comparison = bench.throughput.find(
                      (cell) =>
                        cell.modelId === model.id &&
                        cell.runtime === runtime &&
                        cell.concurrency === concurrency,
                    );
                    if (!comparison) {
                      return (
                        <span
                          className="perf-heat-cell is-empty mono"
                          key={`${runtime}-${concurrency}`}
                          aria-label={`${runtimeName(bench, runtime)} is not available for ${model.model}`}
                        >
                          —
                        </span>
                      );
                    }
                    const multiple = throughputMultiple(comparison);
                    return (
                      <span
                        className={`perf-heat-cell is-${runtime} mono tabular`}
                        key={`${runtime}-${concurrency}`}
                        style={heatStyle(multiple)}
                        aria-label={`Photon throughput is ${multiple.toFixed(2)} times ${runtimeName(bench, runtime)} throughput for ${model.model} at concurrency ${concurrency}`}
                      >
                        {formatMultiple(multiple)}
                      </span>
                    );
                  },
                );
                return [label, ...values];
              })}
            </div>
          </section>
        ))}
      </div>
    </>
  );
};

// ---- 02 · Latency and jitter: runtime × percentile heatmaps per model ----
const LatencyChart = ({ benchmark: bench }: { benchmark: BenchmarkDataset }) => {
  const percentiles = ["p50", "p95", "p99"] as const;
  const baselines = BASELINES.filter((runtime) =>
    bench.latency.some((comparison) => comparison.runtime === runtime),
  );
  const heatStyle = (normalized: number) =>
    ({
      "--perf-heat": `${24 + Math.max(0, Math.min(1, normalized)) * 24}%`,
    }) as CSSProperties;
  const formatLatency = (milliseconds: number) =>
    milliseconds < 1000
      ? `${Math.round(milliseconds)}ms`
      : `${(milliseconds / 1000).toFixed(milliseconds >= 10000 ? 1 : 2)}s`;
  return (
    <>
      <BarLegend
        benchmark={bench}
        runtimes={["photon", ...baselines]}
        note="Brighter cells are faster within each percentile · lower is better"
      />
      <div className="tp-heatmap-list latency-heatmap-list">
        {bench.latency.map((comparison) => {
          const model = bench.models.find(
            (candidate) => candidate.id === comparison.modelId,
          );
          const rows = [
            { runtime: "photon" as const, values: comparison.photon },
            { runtime: comparison.runtime, values: comparison.baseline },
          ];
          return (
            <section
              className="tp-heatmap latency-heatmap"
              key={`${comparison.modelId}-${comparison.runtime}`}
            >
              <div className="tp-heatmap-head">
                <span className="tp-heatmap-name">
                  {model?.model ?? comparison.modelId}
                </span>
                <span className="tp-heatmap-meta mono">
                  c{comparison.concurrency} · rev {model?.revision ?? "—"}
                </span>
              </div>
              <div className="latency-heatmap-grid">
                <span className="tp-heatmap-corner mono">Runtime</span>
                {percentiles.map((percentile) => (
                  <span className="tp-heatmap-column mono" key={percentile}>
                    {percentile}
                  </span>
                ))}
                {rows.flatMap(({ runtime, values }) => {
                  const label = (
                    <span className="tp-heatmap-runtime mono" key={runtime}>
                      <i className={`perf-swatch is-${runtime}`} />
                      {runtimeName(bench, runtime)}
                    </span>
                  );
                  const cells = percentiles.map((percentile) => {
                    const photonValue = comparison.photon[percentile];
                    const baselineValue = comparison.baseline[percentile];
                    const fastest = Math.min(photonValue, baselineValue);
                    const slowest = Math.max(photonValue, baselineValue);
                    const value = values[percentile];
                    const score =
                      fastest === slowest
                        ? 1
                        : (slowest - value) / (slowest - fastest);
                    return (
                      <span
                        className={`perf-heat-cell latency-heat-cell is-${runtime} mono tabular`}
                        key={`${runtime}-${percentile}`}
                        style={heatStyle(score)}
                        aria-label={`${runtimeName(bench, runtime)} ${percentile} completion latency for ${model?.model ?? comparison.modelId}: ${value.toFixed(2)} milliseconds`}
                      >
                        {formatLatency(value)}
                      </span>
                    );
                  });
                  return [label, ...cells];
                })}
              </div>
            </section>
          );
        })}
      </div>
    </>
  );
};

// ---- 03 · Cold start: runtime markers on a shared time axis ----
const ColdStartChart = ({ benchmark: bench }: { benchmark: BenchmarkDataset }) => {
  const modelIds = [...new Set(bench.coldStart.map((row) => row.modelId))];
  const baselines = BASELINES.filter((runtime) =>
    bench.coldStart.some((comparison) => comparison.runtime === runtime),
  );
  const runtimes: BenchRuntime[] = ["photon", ...baselines];
  const allTotals = bench.coldStart.flatMap((comparison) => [
    comparison.photonSeconds,
    comparison.baselineSeconds,
  ]);
  const axisMax = Math.ceil(Math.max(...allTotals) / 50) * 50;
  const ticks = Array.from({ length: 5 }, (_, index) =>
    Math.round((axisMax * index) / 4),
  );
  const formatSeconds = (seconds: number) =>
    `${seconds < 100 ? seconds.toFixed(1) : Math.round(seconds)}s`;
  return (
    <>
      <div className="cold-timeline-legend">
        {runtimes.map((runtime) => (
          <span className="cold-timeline-key" key={runtime}>
            <Image
              src={RUNTIME_LOGOS[runtime]}
              alt=""
              width={18}
              height={18}
            />
            {runtimeName(bench, runtime)}
          </span>
        ))}
        <span className="perf-key-note mono">
          Shared scale across models · lower is better
        </span>
      </div>
      <div className="cold-timeline">
        <div className="cold-timeline-header">
          <span className="cold-timeline-corner mono">Model</span>
          <div className="cold-timeline-axis" aria-hidden="true">
            {ticks.map((tick) => (
              <span
                className="mono tabular"
                key={tick}
                style={{ left: `${(tick / axisMax) * 100}%` }}
              >
                {tick}s
              </span>
            ))}
          </div>
        </div>
        {modelIds.map((modelId) => {
          const model = bench.models.find(
            (candidate) => candidate.id === modelId,
          );
          const comparisons = bench.coldStart.filter(
            (comparison) => comparison.modelId === modelId,
          );
          const totalForRuntime = (runtime: BenchRuntime) =>
            runtime === "photon"
              ? comparisons[0].photonSeconds
              : comparisons.find(
                  (comparison) => comparison.runtime === runtime,
                )?.baselineSeconds;
          return (
            <div className="cold-timeline-row" key={modelId}>
              <span className="cold-timeline-model">
                <strong>{model?.model ?? modelId}</strong>
                <small className="mono">rev {model?.revision ?? "—"}</small>
              </span>
              <div className="cold-timeline-plot">
                <span className="cold-timeline-track" aria-hidden="true" />
                {ticks.map((tick) => (
                  <i
                    className="cold-timeline-gridline"
                    key={tick}
                    style={{ left: `${(tick / axisMax) * 100}%` }}
                    aria-hidden="true"
                  />
                ))}
                {runtimes.map((runtime) => {
                  const total = totalForRuntime(runtime);
                  if (total === undefined) return null;
                  const position = (total / axisMax) * 100;
                  const label = runtimeName(bench, runtime);
                  return (
                    <span
                      className={`cold-timeline-marker is-${runtime}${
                        position > 84 ? " is-edge" : ""
                      }`}
                      key={runtime}
                      style={
                        {
                          "--cold-position": `${position}%`,
                        } as CSSProperties
                      }
                      aria-label={`${label} time to first response for ${model?.model ?? modelId}: ${total.toFixed(2)} seconds`}
                      role="img"
                      title={`${label}: ${total.toFixed(2)} seconds`}
                    >
                      <i className="cold-timeline-stem" aria-hidden="true" />
                      <span className="cold-timeline-logo">
                        <Image
                          src={RUNTIME_LOGOS[runtime]}
                          alt=""
                          width={20}
                          height={20}
                        />
                      </span>
                      <span className="cold-timeline-value mono tabular">
                        {formatSeconds(total)}
                      </span>
                    </span>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};

// ---- 04 · Resource use: VRAM, power, temperature, energy per token ----
const ResourceChart = ({ benchmark: bench }: { benchmark: BenchmarkDataset }) => {
  return (
    <div className="matrix-panel perf-resource-panel">
      <table className="matrix-table">
        <thead>
          <tr>
            <th>Model</th>
            <th>VRAM</th>
            <th>Power</th>
            <th>Temp</th>
            <th>J / output token</th>
          </tr>
        </thead>
        <tbody>
          {bench.models.map((model) => {
            const row = bench.resources.find(
              (resource) =>
                resource.modelId === model.id && resource.runtime === "photon",
            );
            if (!row) throw new Error(`Missing Photon resource row: ${model.id}`);
            return (
              <tr key={model.id}>
                <td>
                  <span className="matrix-model-name">{model.model}</span>
                </td>
                <td className="mono tabular matrix-dim">
                  {row.vramGb.toFixed(1)} GB
                </td>
                <td className="mono tabular matrix-dim">{row.powerW} W</td>
                <td className="mono tabular matrix-dim">{row.tempC} °C</td>
                <td className="mono tabular">{row.joulesPerToken.toFixed(2)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export function PerformancePage() {
  const { resolvedTheme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  const [benchmarkId, setBenchmarkId] = useState(BENCHMARKS[0].id);

  useEffect(() => setMounted(true), []);

  const theme: "dark" | "light" =
    mounted && resolvedTheme === "dark" ? "dark" : "light";

  const bench =
    BENCHMARKS.find((b) => b.id === benchmarkId) ?? BENCHMARKS[0];
  const env = bench.environment;
  const photonResourceRepro = env.repro.replace(
    /--engines\s+\S+/,
    "--engines photon",
  );

  return (
    <div className="md-landing">
      <Nav
        theme={theme}
        onToggleTheme={() => setTheme(theme === "dark" ? "light" : "dark")}
      />

      <main>
        <section
          className="section"
          style={{ paddingTop: 72, paddingBottom: 56 }}
        >
          <div className="container">
            <div
              className="t-eyebrow"
              style={{ color: "var(--text-tertiary)" }}
            >
              Photon
            </div>
            <h1
              className="t-display-md"
              style={{ marginTop: 12, maxWidth: 780 }}
            >
              Photon performance.
            </h1>
            <p
              className="t-body-lg"
              style={{
                color: "var(--text-secondary)",
                marginTop: 16,
                maxWidth: 680,
              }}
            >
              Measured throughput, latency, startup, memory, power, and energy
              use on H100. Results include matched vLLM and SGLang runs, with
              setup and reproduction commands below. {" "}
              <Link href="/blog/photon-2-launch">The launch post</Link> provides
              more context.
            </p>
          </div>
        </section>

        {/* Selected environment + disclosures — visible, not footnoted */}
        <section
          className="section"
          style={{ paddingTop: 0, paddingBottom: 64 }}
        >
          <div className="container">
            <div className="env-panel">
              <div className="env-head">
                <label className="filter-field">
                  <span className="filter-label mono">Benchmark</span>
                  <select
                    className="filter-select"
                    value={benchmarkId}
                    onChange={(e) => setBenchmarkId(e.target.value)}
                  >
                    {BENCHMARKS.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.environment.label} · {b.environment.workload} ·{" "}
                        {formatMatrixDate(b.environment.date)}
                      </option>
                    ))}
                  </select>
                </label>
                <div className="honest-card env-caveat">
                  <div
                    className="t-eyebrow"
                    style={{ color: "var(--text-tertiary)" }}
                  >
                    Scope
                  </div>
                  <p
                    className="t-body-sm"
                    style={{ color: "var(--text-secondary)", margin: "8px 0 0" }}
                  >
                    {env.qualityCaveat}
                  </p>
                </div>
              </div>
              <dl className="env-grid">
                <div>
                  <dt>GPU</dt>
                  <dd>
                    <Link
                      href={`/photon/hardware/${slugify(env.label)}`}
                      className="matrix-hw-link"
                    >
                      {env.gpu}
                    </Link>
                  </dd>
                </div>
                <div>
                  <dt>Engines</dt>
                  <dd>
                    {Object.values(env.runtimes).join(" · ")}
                  </dd>
                </div>
                <div>
                  <dt>Workload</dt>
                  <dd>{env.workload}</dd>
                </div>
                <div>
                  <dt>Concurrency</dt>
                  <dd>{env.concurrency.join(", ")}</dd>
                </div>
                <div>
                  <dt>Updated</dt>
                  <dd>{formatMatrixDate(env.date)}</dd>
                </div>
              </dl>
              <ul className="env-disclosures">
                {env.disclosures.map((disclosure) => (
                  <li key={disclosure}>
                    <IconCheck size={14} /> {disclosure}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Charts */}
        <section className="section" style={{ paddingTop: 0 }}>
          <div className="container perf-stack">
            <ChartPanel
              eyebrow="01 · Request throughput"
              title="Throughput by concurrency"
              sub={`${bench.throughput.length} matched runtime pairs across ${bench.models.length} models and concurrency ${env.concurrency.join(", ")}. Values show Photon throughput relative to each matched runtime.`}
              benchmark={bench}
              sampling={env.sampling.throughput}
              repro={`${env.repro} --chart throughput`}
              onDownload={() =>
                downloadCsv(
                  `photon-${bench.id}-${env.label.toLowerCase()}-throughput.csv`,
                  [
                    "model",
                    "concurrency",
                    "competitor",
                    "multiple",
                    "photon_rps_if_retained",
                    "competitor_rps_if_retained",
                    "advantage_pct",
                  ],
                  bench.throughput.map((c) => [
                    bench.models.find((m) => m.id === c.modelId)?.model ?? c.modelId,
                    c.concurrency,
                    bench.environment.runtimes[c.runtime],
                    c.multiple,
                    c.photonRps ?? "",
                    c.baselineRps ?? "",
                    +throughputAdvantagePct(c).toFixed(2),
                  ]),
                )
              }
            >
              <ThroughputChart benchmark={bench} />
            </ChartPanel>

            <ChartPanel
              eyebrow="02 · Latency & jitter"
              title="Request completion latency"
              sub="Client-observed p50, p95, and p99 at concurrency 1."
              benchmark={bench}
              sampling={env.sampling.latency}
              repro={`${env.repro} --chart latency`}
              onDownload={() =>
                downloadCsv(
                  `photon-${bench.id}-${env.label.toLowerCase()}-latency.csv`,
                  [
                    "model",
                    "concurrency",
                    "runtime",
                    "p50_ms",
                    "p95_ms",
                    "p99_ms",
                    "spread_ms",
                  ],
                  bench.latency.flatMap((comparison) =>
                    ([
                      ["photon", comparison.photon],
                      [comparison.runtime, comparison.baseline],
                    ] as const).map(([runtime, values]) => [
                      bench.models.find((m) => m.id === comparison.modelId)
                        ?.model ?? comparison.modelId,
                      comparison.concurrency,
                      bench.environment.runtimes[runtime],
                      values.p50,
                      values.p95,
                      values.p99,
                      latencySpread(values),
                    ]),
                  ),
                )
              }
            >
              <LatencyChart benchmark={bench} />
            </ChartPanel>

            <ChartPanel
              eyebrow="03 · Cold start"
              title="Time to first response"
              sub="Total time from process start to first response. Results cover seven models for Photon and vLLM, and six for SGLang."
              benchmark={bench}
              sampling={env.sampling.coldStart}
              repro={`${env.repro} --chart cold-start`}
              onDownload={() =>
                downloadCsv(
                  `photon-${bench.id}-${env.label.toLowerCase()}-cold-start.csv`,
                  [
                    "model",
                    "competitor",
                    "photon_total_s",
                    "competitor_api_ready_s_if_recorded",
                    "competitor_total_s",
                    "photon_reduction_pct",
                  ],
                  bench.coldStart.map((comparison) => [
                    bench.models.find((m) => m.id === comparison.modelId)
                      ?.model ?? comparison.modelId,
                    bench.environment.runtimes[comparison.runtime],
                    comparison.photonSeconds,
                    comparison.baselineReadySeconds ?? "",
                    comparison.baselineSeconds,
                    +(
                      (1 -
                        comparison.photonSeconds /
                          comparison.baselineSeconds) *
                      100
                    ).toFixed(2),
                  ]),
                )
              }
            >
              <ColdStartChart benchmark={bench} />
            </ChartPanel>

            <ChartPanel
              eyebrow="04 · Resource use"
              title="Memory, power, and energy per token"
              sub="Measured GPU memory, power, temperature, and energy per output token."
              benchmark={bench}
              runtimes={["photon"]}
              sampling={env.sampling.resources}
              repro={`${photonResourceRepro} --chart resources`}
              onDownload={() =>
                downloadCsv(
                  `photon-${bench.id}-${env.label.toLowerCase()}-resources.csv`,
                  [
                    "model",
                    "vram_gb",
                    "power_w",
                    "temp_c",
                    "joules_per_output_token",
                  ],
                  bench.resources
                    .filter((resource) => resource.runtime === "photon")
                    .map((resource) => [
                      bench.models.find((m) => m.id === resource.modelId)?.model ??
                        resource.modelId,
                      resource.vramGb,
                      resource.powerW,
                      resource.tempC,
                      resource.joulesPerToken,
                    ]),
                )
              }
            >
              <ResourceChart benchmark={bench} />
            </ChartPanel>
          </div>
        </section>

        <section className="section final-cta" style={{ paddingTop: 0 }}>
          <div className="container">
            <div className="final-card perf-final-card card">
              <div className="final-inner">
                <div>
                  <div
                    className="t-eyebrow"
                    style={{ color: "var(--text-tertiary)" }}
                  >
                    Your workload
                  </div>
                  <h2 className="t-heading-xl" style={{ marginTop: 10 }}>
                    Test Photon on your stack.
                  </h2>
                  <p
                    className="t-body-lg"
                    style={{
                      color: "var(--text-secondary)",
                      marginTop: 12,
                      maxWidth: 620,
                    }}
                  >
                    Use the published command as a starting point, or talk to
                    us about your model, hardware, and latency target.
                  </p>
                  <div style={{ marginTop: 24 }}>
                    <InstallSnippet />
                  </div>
                </div>
                <div className="final-ctas">
                  <a href={CONTACT_URL} className="btn btn-secondary">
                    Talk to us
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
